import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Briefcase, Lightbulb, Stethoscope, Building2, Church, Megaphone, Check, AlertCircle, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import ImageUpload from "@/components/ImageUpload";

const NAME_LABEL_BY_KIND = {
  business: "Nombre del negocio",
  venture: "Nombre del emprendimiento",
  professional: "Nombre completo (tú)",
  ngo: "Nombre de la fundación / ONG",
  church: "Nombre de la iglesia",
  community_leader: "Nombre del líder",
};
const NAME_LABEL_BY_KIND_EN = {
  business: "Business name",
  venture: "Venture name",
  professional: "Your full name",
  ngo: "Foundation / NGO name",
  church: "Church name",
  community_leader: "Leader name",
};
const OWNER_LABEL_BY_KIND = {
  ngo: "Representante legal",
  church: "Pastor / Líder",
};
const DESCRIPTION_PH_BY_KIND = {
  professional: "Cuéntale a tus pacientes/clientes tu experiencia, especialidades y enfoque.",
  business: "Describe qué ofreces, qué te hace único y por qué deberían elegirte.",
  venture: "Cuenta la historia de tu emprendimiento y qué problema resuelves.",
  ngo: "Misión, visión y a quién ayuda tu organización.",
  church: "Quiénes son, horarios de servicio y ministerios.",
  community_leader: "Tu causa, comunidad y trabajo social.",
};

const KIND_LIST = [
  { value: "business", Icon: Briefcase, color: "bg-orange" },
  { value: "venture", Icon: Lightbulb, color: "bg-amber-500" },
  { value: "professional", Icon: Stethoscope, color: "bg-teal" },
  { value: "ngo", Icon: Building2, color: "bg-teal-deep" },
  { value: "church", Icon: Church, color: "bg-violet-600" },
  { value: "community_leader", Icon: Megaphone, color: "bg-rose-500" },
];

const KIND_LABEL = {
  business: "kindBusiness",
  venture: "kindVenture",
  professional: "kindProfessional",
  ngo: "kindNgo",
  church: "kindChurch",
  community_leader: "kindCommunityLeader",
};

const EMPTY = {
  kind: "",
  name: "",
  owner_name: "",
  category: "",
  description: "",
  phone: "",
  whatsapp: "",
  email: "",
  city: "",
  state: "",
  country: "USA",
  address: "",
  website: "",
  logo_url: "",
  cover_url: "",
  cover_position: 50,
  facebook: "",
  instagram: "",
  twitter: "",
  linkedin: "",
  tiktok: "",
  youtube: "",
  volunteer: false,
};

const ProfileForm = ({ mode = "create" }) => {
  const { t, lang } = useI18n();
  const isSpanish = lang === "es";
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isWelcome = searchParams.get("welcome") === "1";
  const prefKind = searchParams.get("kind") || "";
  const isEdit = mode === "edit";

  const [step, setStep] = useState(() => {
    if (isEdit) {
      // Deep-link: /profiles/edit/{id}?focus=media jumps to step 3
      if (searchParams.get("focus") === "media") return 3;
      return 2;
    }
    // If a valid kind is preselected via ?kind=, skip step 1 (kind chooser)
    if (prefKind && KIND_LIST.some((k) => k.value === prefKind)) return 2;
    return 1;
  });
  const hasPrefKind = !!prefKind && KIND_LIST.some((k) => k.value === prefKind);
  const initialOwner = (user && typeof user === "object" && user.full_name) || "";
  const [form, setForm] = useState(() => {
    const base = !isEdit && hasPrefKind ? { ...EMPTY, kind: prefKind } : EMPTY;
    // Pre-fill owner_name (and name for professional kind) with the account full_name so the user doesn't retype it.
    if (!isEdit && initialOwner) {
      base.owner_name = initialOwner;
      if (base.kind === "professional") base.name = initialOwner;
    }
    return base;
  });

  // If the user object hydrates AFTER the form mounted, back-fill defaults once
  useEffect(() => {
    if (isEdit) return;
    if (!user || !user.full_name) return;
    setForm((f) => {
      const next = { ...f };
      if (!f.owner_name) next.owner_name = user.full_name;
      if (f.kind === "professional" && !f.name) next.name = user.full_name;
      return next;
    });
  }, [user]);
  // Fallback: derive categories from i18n so the dropdown is never empty
  // even if the /categories endpoint is slow or fails.
  const fallbackCategories = useMemo(() => Object.keys(t.categories || {}), [t]);
  const [categories, setCategories] = useState(fallbackCategories);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Fetch categories (overrides fallback with authoritative list from backend)
  useEffect(() => {
    api.get("/categories")
      .then(({ data }) => { if (Array.isArray(data.categories) && data.categories.length) setCategories(data.categories); })
      .catch(() => {});
  }, []);

  // Load existing profile when editing
  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    api.get(`/profiles/${id}`)
      .then(({ data }) => setForm({ ...EMPTY, ...data }))
      .catch((err) => setError(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [isEdit, id]);

  const kindMeta = useMemo(() => KIND_LIST.find((k) => k.value === form.kind), [form.kind]);

  // Auth guard
  if (user === null) return <div className="container-tight py-24 text-center text-teal-soft">…</div>;
  if (user === false) { navigate("/login"); return null; }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const showCategory = ["business", "venture", "professional"].includes(form.kind);
  const categoryRequired = ["business", "venture"].includes(form.kind);

  const validateStep2 = () => {
    if (!form.kind) return "Selecciona el tipo de perfil.";
    if (!form.name.trim() || form.name.trim().length < 2) return "Nombre requerido (mín. 2).";
    if (!form.description.trim() || form.description.trim().length < 10) return "Descripción mínima 10 caracteres.";
    if (!form.city.trim()) return "Ciudad requerida.";
    if (categoryRequired && !form.category) return "Selecciona una categoría.";
    return null;
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    const msg = validateStep2();
    if (msg) { setError(msg); setStep(2); return; }

    const payload = { ...form };
    // Backend EmailStr rejects "" — send undefined if empty
    if (!payload.email) delete payload.email;

    setLoading(true);
    try {
      let savedId = id;
      if (isEdit) {
        await api.put(`/profiles/${id}`, payload);
      } else {
        const { data } = await api.post("/profiles", payload);
        savedId = data?.id;
      }
      // After a fresh-from-welcome profile creation, send the user to the
      // most useful landing depending on their kind:
      //   professional  → agenda (open first slots)
      //   business/venture → catalog (add first product)
      //   ngo/church/community_leader → my-profiles
      if (!isEdit && isWelcome && savedId) {
        if (form.kind === "professional") {
          navigate(`/profiles/${savedId}/agenda?welcome=1`);
        } else if (form.kind === "business" || form.kind === "venture") {
          navigate(`/profiles/${savedId}/catalog?welcome=1`);
        } else {
          navigate("/my-profiles");
        }
      } else {
        navigate("/my-profiles");
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section bg-cream min-h-[80vh]">
      <div className="container-tight max-w-3xl">
        {!isWelcome && (
          <Link to="/my-profiles" className="text-sm text-teal-soft hover:text-orange inline-flex items-center gap-1" data-testid="back-to-panel">
            <ArrowLeft size={14} /> {t.myProfiles.backToPanel}
          </Link>
        )}
        {isWelcome && !isEdit && (
          <div className="mt-2 bg-gradient-to-r from-teal-deep to-orange text-white rounded-2xl p-5 flex items-start gap-3" data-testid="welcome-banner">
            <span className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <Sparkles size={18} />
            </span>
            <div>
              <p className="font-display text-xl leading-tight">{t.myProfiles.welcomeTitle}</p>
              <p className="text-white/85 text-sm mt-1">{t.myProfiles.welcomeBody}</p>
            </div>
          </div>
        )}
        <p className="eyebrow text-orange mt-4">{isEdit ? t.myProfiles.formEditTitle : t.myProfiles.formNewTitle}</p>
        <h1 className="font-display text-4xl md:text-5xl text-teal-deep leading-tight mt-2">
          {isEdit ? t.myProfiles.formEditTitle : t.myProfiles.formNewTitle}
        </h1>
        {!isEdit && <p className="text-teal-soft mt-2">{t.myProfiles.formNewSub}</p>}

        {/* Steps */}
        <div className="mt-8 flex items-center gap-2 flex-wrap" data-testid="form-stepper">
          {!hasPrefKind && (
            <>
              <StepDot n={1} label={t.myProfiles.stepKind} active={step >= 1} done={step > 1} onClick={() => !isEdit && setStep(1)} clickable={!isEdit} />
              <span className="text-gray-300">—</span>
            </>
          )}
          <StepDot n={hasPrefKind ? 1 : 2} label={t.myProfiles.stepDetails} active={step >= 2} done={step > 2} onClick={() => form.kind && setStep(2)} clickable={!!form.kind} />
          <span className="text-gray-300">—</span>
          <StepDot n={hasPrefKind ? 2 : 3} label={t.myProfiles.stepBrand} active={step >= 3} done={false} onClick={() => form.kind && setStep(3)} clickable={!!form.kind} />
        </div>

        {error && (
          <p className="mt-4 text-red-600 text-sm inline-flex items-center gap-2" data-testid="form-error">
            <AlertCircle size={14} /> {error}
          </p>
        )}

        <form className="mt-6 bg-white rounded-2xl border border-gray-200 p-6 md:p-8" onSubmit={submit}>
          {step === 1 && (
            <div data-testid="step-kind">
              <p className="text-sm text-teal-soft mb-4">{t.myProfiles.kindHint}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {KIND_LIST.map((k) => {
                  const Icon = k.Icon;
                  const selected = form.kind === k.value;
                  return (
                    <button type="button" key={k.value}
                      onClick={() => set("kind", k.value)}
                      data-testid={`kind-option-${k.value}`}
                      className={`text-left rounded-xl border-2 p-4 transition-all flex gap-3 items-start ${selected ? "border-orange bg-orange/5" : "border-gray-200 hover:border-teal/50"}`}>
                      <span className={`w-10 h-10 rounded-full ${k.color} text-white flex items-center justify-center shrink-0`}>
                        <Icon size={18} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-lg text-teal-deep">{t.myProfiles[KIND_LABEL[k.value]]}</div>
                        <div className="text-xs text-teal-soft mt-1">{t.myProfiles[`${KIND_LABEL[k.value]}Desc`]}</div>
                      </div>
                      {selected && <Check size={18} className="text-orange shrink-0" />}
                    </button>
                  );
                })}
              </div>
              <div className="mt-6 flex justify-end">
                <button type="button" disabled={!form.kind} onClick={() => setStep(2)} className="btn-orange disabled:opacity-50" data-testid="step1-next">
                  {t.myProfiles.next}
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="step-details">
              {kindMeta && (
                <div className="flex items-center gap-2 bg-cream rounded-lg px-3 py-2 text-sm">
                  <span className={`w-7 h-7 rounded-full ${kindMeta.color} text-white flex items-center justify-center`}>
                    <kindMeta.Icon size={13} />
                  </span>
                  <span className="text-teal-deep font-semibold">{t.myProfiles[KIND_LABEL[form.kind]]}</span>
                  {!isEdit && !hasPrefKind && (
                    <button type="button" onClick={() => setStep(1)} className="ml-auto text-xs text-orange hover:underline" data-testid="change-kind">
                      {t.myProfiles.changeKind}
                    </button>
                  )}
                </div>
              )}
              <Field label={(isSpanish ? NAME_LABEL_BY_KIND : NAME_LABEL_BY_KIND_EN)[form.kind] + " *"} testid="f-name">
                <input className="field-input" value={form.name} onChange={(e) => set("name", e.target.value)} required maxLength={120} data-testid="input-name" />
              </Field>
              {OWNER_LABEL_BY_KIND[form.kind] && (
                <Field label={OWNER_LABEL_BY_KIND[form.kind]} testid="f-owner">
                  <input className="field-input" value={form.owner_name} onChange={(e) => set("owner_name", e.target.value)} maxLength={120} data-testid="input-owner" />
                </Field>
              )}
              {showCategory && (
                <Field label={t.fields.category + (categoryRequired ? " *" : "")} testid="f-category">
                  <select
                    className="field-input"
                    value={form.category}
                    onChange={(e) => set("category", e.target.value)}
                    required={categoryRequired}
                    data-testid="input-category"
                  >
                    <option value="" disabled>
                      {categories.length === 0 ? "Cargando categorías…" : "— Selecciona una categoría —"}
                    </option>
                    {categories.map((c) => (
                      <option key={c} value={c}>{t.categories[c] || c}</option>
                    ))}
                  </select>
                </Field>
              )}
              <Field label={t.fields.description + " *"} testid="f-desc">
                <textarea className="field-input min-h-[110px]" value={form.description} onChange={(e) => set("description", e.target.value)} required minLength={10} maxLength={2000} placeholder={DESCRIPTION_PH_BY_KIND[form.kind] || t.fields.placeholders.description} data-testid="input-description" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t.fields.city + " *"} testid="f-city">
                  <input className="field-input" value={form.city} onChange={(e) => set("city", e.target.value)} required maxLength={120} data-testid="input-city" />
                </Field>
                <Field label={t.fields.state} testid="f-state">
                  <input className="field-input" value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="input-state" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t.fields.phone} testid="f-phone">
                  <input className="field-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="input-phone" />
                </Field>
                <Field label={t.fields.whatsapp} hint={t.fields.whatsappHint} testid="f-wa">
                  <input className="field-input" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} data-testid="input-whatsapp" />
                </Field>
              </div>
              <Field label="Email" testid="f-email">
                <input type="email" className="field-input" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="input-email" />
              </Field>

              {(form.kind === "professional" || form.kind === "community_leader") && (
                <label className="flex items-start gap-2 text-sm bg-cream rounded-lg p-3">
                  <input type="checkbox" checked={!!form.volunteer} onChange={(e) => set("volunteer", e.target.checked)} className="mt-0.5" data-testid="input-volunteer" />
                  <span>
                    <span className="font-semibold text-teal-deep">{t.auth.volunteerLabel}</span>
                    <span className="block text-teal-soft text-xs mt-0.5">{t.auth.volunteerHint}</span>
                  </span>
                </label>
              )}

              <div className="flex justify-between gap-2 pt-2">
                {!isEdit && !hasPrefKind && (
                  <button type="button" onClick={() => setStep(1)} className="btn-teal-outline" data-testid="step2-back">{t.myProfiles.back}</button>
                )}
                <div className="ml-auto flex gap-2">
                  <button type="button" onClick={() => setStep(3)} className="btn-teal-outline" data-testid="step2-next">{t.myProfiles.next}</button>
                  <button type="submit" disabled={loading} className="btn-orange disabled:opacity-60" data-testid="submit-profile">
                    {loading ? t.myProfiles.saving : (isEdit ? t.myProfiles.updateBtn : t.myProfiles.createBtn)}
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="step-brand">
              <p className="text-sm text-teal-soft">{t.fields.step3OptionalNote}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t.fields.website} testid="f-web">
                  <input className="field-input" value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="input-website" />
                </Field>
                <Field label={t.fields.address} testid="f-addr">
                  <input className="field-input" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="input-address" />
                </Field>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 pt-2" data-testid="branding-uploads">
                <ImageUpload
                  label={t.fields.logoUrl}
                  hint="Sube el logo desde tu celular o pega una URL"
                  value={form.logo_url || ""}
                  onChange={(v) => set("logo_url", v)}
                  aspect="square"
                  purpose="logo"
                  testid="upload-logo"
                />
                <ImageUpload
                  label={t.fields.coverUrl}
                  hint="Imagen ancha que se muestra en tu perfil"
                  value={form.cover_url || ""}
                  onChange={(v) => set("cover_url", v)}
                  aspect="wide"
                  purpose="cover"
                  positionY={form.cover_position ?? 50}
                  onPositionYChange={(v) => set("cover_position", v)}
                  testid="upload-cover"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4 pt-2">
                <Field label={t.fields.facebook} testid="f-fb">
                  <input className="field-input" value={form.facebook} onChange={(e) => set("facebook", e.target.value)} data-testid="input-facebook" />
                </Field>
                <Field label={t.fields.instagram} testid="f-ig">
                  <input className="field-input" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} data-testid="input-instagram" />
                </Field>
                <Field label={t.fields.linkedin} testid="f-in">
                  <input className="field-input" value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} data-testid="input-linkedin" />
                </Field>
                <Field label={t.fields.tiktok} testid="f-tt">
                  <input className="field-input" value={form.tiktok} onChange={(e) => set("tiktok", e.target.value)} data-testid="input-tiktok" />
                </Field>
              </div>

              <div className="flex justify-between gap-2 pt-2">
                <button type="button" onClick={() => setStep(2)} className="btn-teal-outline" data-testid="step3-back">{t.myProfiles.back}</button>
                <button type="submit" disabled={loading} className="btn-orange disabled:opacity-60" data-testid="submit-profile-final">
                  {loading ? t.myProfiles.saving : (isEdit ? t.myProfiles.updateBtn : t.myProfiles.createBtn)}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </section>
  );
};

const StepDot = ({ n, label, active, done, onClick, clickable }) => (
  <button type="button" onClick={clickable ? onClick : undefined} className={`inline-flex items-center gap-2 ${clickable ? "cursor-pointer" : "cursor-default"}`}>
    <span className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center ${active ? "bg-orange text-white" : "bg-gray-200 text-gray-500"}`}>
      {done ? <Check size={12} /> : n}
    </span>
    <span className={`text-xs uppercase tracking-wider ${active ? "text-teal-deep font-bold" : "text-gray-400"}`}>{label}</span>
  </button>
);

const Field = ({ label, hint, testid, children }) => (
  <div data-testid={testid}>
    <label className="block text-xs uppercase tracking-wider text-teal-soft font-bold mb-1">{label}</label>
    {children}
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

export default ProfileForm;
